import devicesModalStyle from "../devices-modal.module.css";

import { useState, useReducer, useEffect, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";

// Controller
import controller from "../controller";

// Package
import { useTranslation } from "react-i18next";
import { clone, cloneDeep, isEmpty, startsWith } from "lodash";

// UI components
import Icon from "components/Icon";
import Button from "components/Button";
import ModalContainer from "components/ModalContainer";
import ButtonWithIcon from "components/ButtonWithIcon";
import DropdownWithItem from "components/DropdownWithItem";
import SpinnerForModal from "ui/SpinnerForModal";

// Slice
import { uiActions, selectShowModalSpinner } from "stores/slice/ui";

// Store
import licenseConfig from "../stores/licenseConfig";

// Const
import {
  fakeAssignLicenseTermList,
  defaultMessages,
  deviceTypeMapping,
} from "../const";

const AssignLicenseModal = (props) => {
  const {
    modalStatus,
    changeModalStatus,
    formatDeviceListTargetData,
    parentCheckedIDs,
    orgId,
  } = props;

  // License selection State
  const [licenseSelectState, licenseSelectDispatch] = useReducer(
    licenseConfig.reducer,
    licenseConfig.defaultState
  );

  const [selectedDevices, setSelectedDevices] = useState({
    ap: 0,
    gateway: 0,
    switch: 0,
  });
  const [messages, setMessages] = useState(cloneDeep(defaultMessages));
  const { t } = useTranslation();
  const showModalSpinner = useSelector(selectShowModalSpinner);
  const dispatch = useDispatch();

  const getLicense = useCallback(
    async (deviceCount) => {
      try {
        const params = {
          organizationId: orgId,
        };
        const res = await controller.getLicenseApi(params);
        const remainingList = controller.getRemaining(cloneDeep(res.data));
        return [cloneDeep(remainingList), deviceCount];
      } catch (error) {
        setMessages({
          ...messages,
          error: error.code,
          success: null,
        });
      }
    },
    [messages, orgId]
  );

  const setLicense = ([remainingList, deviceCount]) => {
    const clonedLicenseSelectState = cloneDeep(licenseSelectState);
    remainingList.forEach((license) => {
      const deviceType = license["deviceType"];
      if (license.period === 7) {
        clonedLicenseSelectState[deviceType]["licenseTerms"][0]["list"][
          license.period - 2
        ]["totalCount"] += 1;
        clonedLicenseSelectState[deviceType]["licenseTerms"][0]["list"][
          license.period - 2
        ]["remainingCount"] += 1;
      } else {
        clonedLicenseSelectState[deviceType]["licenseTerms"][0]["list"][
          license.period - 1
        ]["totalCount"] += 1;
        clonedLicenseSelectState[deviceType]["licenseTerms"][0]["list"][
          license.period - 1
        ]["remainingCount"] += 1;
      }
      clonedLicenseSelectState[deviceType]["licenseTerms"][0]["selected"] =
        cloneDeep(
          clonedLicenseSelectState[deviceType]["licenseTerms"][0]["list"][0]
        );
      clonedLicenseSelectState[deviceType]["licenseTerms"][0]["selected"][
        "remainingCount"
      ] -= deviceCount[deviceTypeMapping[deviceType]];
    });

    licenseSelectDispatch({
      type: licenseConfig.action.initializeState,
      payload: clonedLicenseSelectState,
    });
  };

  const init = async () => {
    // set selected device count
    let deviceCount = { ap: 0, gateway: 0, switch: 0 };
    formatDeviceListTargetData.forEach((item) => {
      if (parentCheckedIDs.includes(item.id)) {
        deviceCount[item.productCategory.toLowerCase()]++;
      }
    });
    setSelectedDevices({ ...selectedDevices, ...deviceCount });
    setLicense(await getLicense(deviceCount));
  };

  useEffect(() => {
    if (modalStatus.assignLicense.status) {
      dispatch(uiActions.showModalSpinner());
      init();
      setTimeout(() => {
        dispatch(uiActions.hideModalSpinner());
      }, 1000);
    }
  }, [modalStatus.assignLicense.status]);

  return (
    <div className={devicesModalStyle["inventory-assign-license-modal"]}>
      <ModalContainer
        modalWidthType="modal-500px"
        openModal={modalStatus.assignLicense.status}
        closeModal={() => changeModalStatus("assignLicense", false)}
      >
        {showModalSpinner && <SpinnerForModal />}
        <div className="header">
          <div className="title">Assign licenses</div>
        </div>
        <div className="body">
          <div className={devicesModalStyle["device-count-container"]}>
            <span>You have selected</span>
            <span className="mx-1">{selectedDevices.ap}</span>
            <span>AP(s)</span>
            <span>,</span>
            <span className="mx-1">{selectedDevices.switch}</span>
            <span>switch(es)</span>
            <span>,</span>
            <span className="mx-1">{selectedDevices.gateway}</span>
            <span>gateway(s)</span>
            <span>.</span>
          </div>

          {/* AP */}
          <div className={devicesModalStyle["license-term-container"]}>
            <div className={devicesModalStyle["title-container"]}>
              Access point
            </div>
            <div className={devicesModalStyle["license-container"]}>
              {/* can loop this div */}
              <div>
                <div className={devicesModalStyle["term-container"]}>
                  <DropdownWithItem
                    id="ap-term-dropdown"
                    type="normal"
                    selectedItem={fakeAssignLicenseTermList[0]}
                    itemList={licenseSelectState["DBA"].licenseTerms[0].list}
                    disabled={selectedDevices.ap === 0 ? true : false}
                    onClick={() => {}}
                  />
                  <div
                    className={`mt-1 ${devicesModalStyle["remain-count"]}`}
                    disabled
                  >
                    <span>Remaining</span>
                    <span className="mx-1">0</span>
                  </div>
                </div>
                <div className={devicesModalStyle["add-container"]}>
                  <ButtonWithIcon
                    label="Add"
                    className="d-flex justify-content-center"
                    iconClassName="icon-expand"
                    disabled={selectedDevices.ap === 0 ? true : false}
                    onClick={() => {}}
                  ></ButtonWithIcon>
                </div>
              </div>
            </div>
          </div>

          {/* SW */}
          <div className={devicesModalStyle["license-term-container"]}>
            <div className={devicesModalStyle["title-container"]}>Switch</div>
            <div className={devicesModalStyle["license-container"]}>
              {/* can loop this div */}
              <div>
                <div className={devicesModalStyle["term-container"]}>
                  <DropdownWithItem
                    id="sw-term-dropdown"
                    type="normal"
                    selectedItem={fakeAssignLicenseTermList[0]}
                    itemList={fakeAssignLicenseTermList}
                    disabled={selectedDevices.switch === 0 ? true : false}
                    onClick={() => {}}
                  />
                  <div className={`mt-1 ${devicesModalStyle["remain-count"]}`}>
                    <span className={devicesModalStyle["insufficient"]}>
                      Insufficient Licenses
                    </span>
                  </div>
                </div>
                <div className={devicesModalStyle["add-container"]}>
                  <ButtonWithIcon
                    label="Add"
                    className="d-flex justify-content-center"
                    iconClassName="icon-expand"
                    disabled={selectedDevices.switch === 0 ? true : false}
                    onClick={() => {}}
                  ></ButtonWithIcon>
                </div>
              </div>
            </div>
          </div>

          {/* GW */}
          <div className={devicesModalStyle["license-term-container"]}>
            <div className={devicesModalStyle["title-container"]}>Gateway</div>
            <div className={devicesModalStyle["license-container"]}>
              {/* can loop this div */}
              <div>
                <div className={devicesModalStyle["term-container"]}>
                  <DropdownWithItem
                    id="gw-term-dropdown"
                    type="normal"
                    selectedItem={
                      licenseSelectState["DBG"].licenseTerms[0]["selected"]
                    }
                    itemList={licenseSelectState["DBG"].licenseTerms[0].list}
                    disabled={selectedDevices.gateway === 0 ? true : false}
                    onClick={() => {}}
                  />
                  {licenseSelectState["DBG"].licenseTerms[0]["selected"] && (
                    <div
                      className={`mt-1 ${devicesModalStyle["remain-count"]}`}
                    >
                      <span>Remaining</span>
                      <span className="mx-1">
                        {
                          licenseSelectState["DBG"].licenseTerms[0]["selected"][
                            "remainingCount"
                          ]
                        }
                      </span>
                    </div>
                  )}
                </div>
                <div className={devicesModalStyle["add-container"]}>
                  <ButtonWithIcon
                    label="Add"
                    className="d-flex justify-content-center"
                    iconClassName="icon-expand"
                    disabled={selectedDevices.gateway === 0 ? true : false}
                    onClick={() => {}}
                  ></ButtonWithIcon>
                </div>
              </div>

              {/* can loop this div */}
              {/* <div>
                <div className={devicesModalStyle["term-container"]}>
                  <DropdownWithItem
                    id="gw-term-dropdown"
                    type="normal"
                    selectedItem={fakeAssignLicenseTermList[0]}
                    itemList={fakeAssignLicenseTermList}
                    onClick={() => {}}
                  />
                  <div className={`mt-1 ${devicesModalStyle["remain-count"]}`}>
                    <span>Remaining</span>
                    <span className="mx-1">9</span>
                  </div>
                </div>
                <div className={devicesModalStyle["add-container"]}>
                  <Icon
                    iconTitle="delete"
                    className={`icon-close ${devicesModalStyle["icon"]}`}
                    onClick={() => {}}
                  />
                </div>
              </div> */}
            </div>
          </div>
        </div>
        <div className={`footer ${devicesModalStyle["footer"]}`}>
          <Button
            label="Cancel"
            className="btn-cancel"
            onClick={() => changeModalStatus("assignLicense", false)}
          />
          <Button label="Assign" className="btn-submit" onClick={() => {}} />
        </div>
      </ModalContainer>
    </div>
  );
};

export default AssignLicenseModal;
